import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { getAccessToken, getRefreshToken, setAccessToken, clearTokens, isPortalViewSession } from '@/lib/utils/tokenStorage';
import { isDemo } from '@/lib/demo/demoMode';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor - Add auth token. In demo mode, swap the adapter so the
// request is answered from the in-browser sandbox and never touches the network.
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (isDemo()) {
      const { demoAdapter } = await import('@/lib/demo/demoAdapter');
      config.adapter = demoAdapter as never;
      return config;
    }
    // An explicitly-set Authorization header wins — the portal-preview window
    // files change requests as the ADMIN even though the realm holds a portal
    // token (see FloatingRequestButton).
    const token = getAccessToken();
    if (token && config.headers && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken } = response.data.data;
        setAccessToken(accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed — clear tokens and return to the RIGHT login. A real
        // client whose session expired used to get dumped on the staff ops
        // login (portal audit); anyone inside /portal goes back to /portal/login.
        clearTokens();
        const inPortal = isPortalViewSession() || window.location.pathname.startsWith('/portal');
        window.location.href = inPortal ? '/portal/login' : '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
export { apiClient };
